import React, { useContext } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
} from "@mui/material";
import { CartContext } from "../context/CartContext";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { setCheckout } from "../utils/buyNowStorage";
import LoadingScreen from "../components/LoadingScreen";
import { colors } from "../utils/theme";

const CartPage = () => {
  const { cart, updateQty, removeItem, initialized } = useContext(CartContext);
  const navigate = useNavigate();

  if (!initialized) {
    return <LoadingScreen message="Loading cart..." />;
  }

  if (cart.length === 0)
    return (
      <Typography sx={{ textAlign: "center", mt: 4, color: colors.textMuted }}>
        Cart is empty
      </Typography>
    );

  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: { xs: 1.5, sm: 3 }, minHeight: "100vh", bgcolor: colors.surfaceBright }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: colors.onSurface, fontSize: { xs: "1.1rem", sm: "1.5rem" } }}>
        Your Cart
      </Typography>

      {cart.map((item, index) => (
        <Box key={item.id}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, p: { xs: 1.5, sm: 2 }, "&:hover": { bgcolor: colors.surfaceContainerLow } }}>
            {/* Product image - clickable, matching My Orders size */}
            <Box
              onClick={() => navigate(`/product/${item.id}`)}
              sx={{ width: 80, height: 80, flexShrink: 0, border: "1px solid", borderColor: colors.outlineVariant, borderRadius: 1, p: 0.5, bgcolor: colors.onPrimary, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden" }}
            >
              <Box
                component="img"
                src={typeof item.imageUrl === "string" ? item.imageUrl : item.imageUrl?.thumbnailUrl}
                alt={item.title}
                sx={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Box>

            {/* Item details */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                onClick={() => navigate(`/product/${item.id}`)}
                sx={{ fontWeight: 600, fontSize: 14, color: colors.textMain, cursor: "pointer", wordBreak: "break-word", overflowWrap: "break-word", "&:hover": { color: colors.primary } }}
              >
                {item.title}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => updateQty(item.id, item.qty - 1)}
                  sx={{ minWidth: 32, height: 32, borderColor: colors.outlineVariant, color: colors.textMain, fontWeight: 700, fontSize: 16, "&:hover": { borderColor: colors.primary } }}
                >
                  −
                </Button>
                <Typography sx={{ minWidth: 24, textAlign: "center", fontWeight: 600, fontSize: 14, color: colors.textMain }}>
                  {item.qty}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => updateQty(item.id, item.qty + 1)}
                  sx={{ minWidth: 32, height: 32, borderColor: colors.outlineVariant, color: colors.textMain, fontWeight: 700, fontSize: 16, "&:hover": { borderColor: colors.primary } }}
                >
                  +
                </Button>
                <IconButton onClick={() => removeItem(item.id)} size="small" sx={{ ml: 1, color: colors.textMuted, "&:hover": { color: colors.error } }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Subtotal */}
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: colors.textMain, whiteSpace: "nowrap", mt: 0.5 }}>
              ₹{Number(item.price * item.qty).toLocaleString("en-IN")}
            </Typography>
          </Box>
          {index < cart.length - 1 && <Divider sx={{ borderColor: colors.outlineVariant, mx: { xs: 1.5, sm: 2 } }} />}
        </Box>
      ))}

      <Divider sx={{ my: 2, borderColor: colors.outlineVariant }} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: colors.textMain }}>
          Total: <span style={{ color: colors.error, fontSize: "1.2em" }}>₹{Number(total).toLocaleString("en-IN")}</span>
        </Typography>

        <Button
          variant="contained"
          sx={{ bgcolor: colors.primary, textTransform: "none", fontWeight: 600, borderRadius: 1, py: 1, px: 3, "&:hover": { bgcolor: colors.primaryContainer } }}
          onClick={() => {
            setCheckout({
              mode: "CART",
              items: cart.map((i) => ({ ...i })),
            });
            navigate("/delivery-location");
          }}
        >
          Proceed to Checkout →
        </Button>
      </Box>
    </Box>
  );
};

export default CartPage;